// Aurora Innovation, Inc. Proprietary and Confidential. Copyright 2026.
import { common } from '../../tokens/primitiveColors/common';
import { transparency } from '../../tokens/primitiveColors/transparency';
import { background } from '../../tokens/themeColors/background';
import { border } from '../../tokens/themeColors/border';
import { gradient, toCssGradient } from '../../tokens/themeColors/gradient';
import { icon } from '../../tokens/themeColors/icon';
import { inverseBackground, inverseBorder, inverseIcon, inverseText } from '../../tokens/themeColors/inverse';
import { text } from '../../tokens/themeColors/text';
export var buttonColors = function(mode) {
    var bg = background(mode);
    var bd = border(mode);
    var ic = icon(mode);
    var tx = text(mode);
    var gr = gradient(mode);
    // Every inverted* variant renders on a high-contrast surface and reads from the Inverse
    // token set rather than the regular one; `bg`/`bd`/`ic`/`tx` above drive the parts that
    // stay on the current theme.
    var inverseBg = inverseBackground(mode);
    var inverseBd = inverseBorder(mode);
    var inverseIc = inverseIcon(mode);
    var inverseTx = inverseText(mode);
    return {
        // Confirmed directly from the rendered component's own CSS variable (both the
        // regular Button and the "Button - Inverse Theme" component's Focus state literally
        // emit `border-[var(--border-colors/focus,#66ABFF)]`) — this is Border Colors/Focus,
        // not Action - Bold.
        focus: {
            border: bd.focus
        },
        primary: {
            default: {
                background: inverseBg.level50,
                icon: inverseIc.heaviest,
                text: tx.action
            },
            hover: {
                background: inverseBg.level300,
                icon: inverseIc.heaviest,
                text: tx.action
            },
            active: {
                background: inverseBg.state.selected3,
                icon: inverseIc.heaviest,
                text: tx.action
            },
            disabled: {
                background: bg.state.disabled2,
                icon: ic.disabled2,
                text: tx.disabled2
            },
            // Figma Active+Disabled; unreachable here (disabled blocks :active), kept as the token source.
            activeDisabled: {
                background: bg.state.disabled3,
                icon: ic.disabled2,
                text: tx.tertiary
            }
        },
        secondary: {
            default: {
                border: bd.default,
                icon: ic.active,
                text: tx.primary
            },
            hover: {
                background: bg.state.hover0
            },
            active: {
                background: bg.state.selectedTransparent,
                border: bd.action200,
                icon: ic.actionAlt,
                text: tx.action100
            },
            disabled: {
                border: bd.soft,
                icon: ic.disabled,
                text: tx.disabled
            },
            // Figma Active+Disabled; unreachable here (disabled blocks :active), kept as the token source.
            activeDisabled: {
                background: bg.state.disabledTransparent,
                border: bd.heavy,
                icon: ic.disabled2,
                text: tx.tertiary
            }
        },
        text: {
            default: {
                icon: ic.active,
                text: tx.primary
            },
            hover: {
                background: bg.state.hover0
            },
            active: {
                background: bg.state.selectedTransparent,
                icon: ic.actionAlt,
                text: tx.action100
            },
            disabled: {
                icon: ic.disabled,
                text: tx.disabled
            },
            // Figma Active+Disabled; unreachable here (disabled blocks :active), kept as the token source.
            activeDisabled: {
                background: bg.state.disabledTransparent,
                icon: ic.disabled2,
                text: tx.tertiary
            }
        },
        translucent: {
            default: {
                background: transparency.gray.dark[500],
                icon: ic.white,
                text: tx.white
            },
            hover: {
                background: transparency.gray.dark[700]
            },
            active: {
                background: transparency.gray.dark[800],
                icon: ic.white,
                text: tx.white
            },
            disabled: {
                background: transparency.gray.dark[300],
                icon: transparency.gray.light[500],
                text: tx.disabled
            },
            // Figma Active+Disabled; unreachable here (disabled blocks :active), kept as the token source.
            activeDisabled: {
                background: transparency.gray.dark[400],
                icon: transparency.gray.light[700],
                text: tx.disabled
            }
        },
        destructive: {
            default: {
                background: bg.status.errorBold2,
                icon: common.white,
                text: common.white
            },
            hover: {
                background: bg.status.hover.errorBold
            },
            active: {
                background: bg.status.active.errorBold
            },
            disabled: {
                background: bg.state.disabled2,
                icon: ic.disabled,
                text: tx.disabled
            },
            // Figma Active+Disabled; unreachable here (disabled blocks :active), kept as the token source.
            activeDisabled: {
                background: bg.state.disabled3,
                icon: ic.disabled2,
                text: tx.tertiary
            }
        },
        // Figma: Button/Secondary/Contained/* — grey-color contained variant, distinct from
        // `primary`. Default/Hover/Active backgrounds are gradients (gradients/gray-linear/*),
        // not flat theme-color fills, so they don't show up in the Component Colors alias
        // chain — confirmed directly against the component spec (node 15341:100724/731/737).
        secondaryContained: {
            default: {
                background: toCssGradient(gr.grayLinear.default),
                icon: ic.active,
                text: inverseTx.action
            },
            hover: {
                background: toCssGradient(gr.grayLinear.hover)
            },
            active: {
                background: toCssGradient(gr.grayLinear.active),
                icon: inverseIc.heaviest,
                text: tx.action
            },
            disabled: {
                background: bg.state.disabled2,
                icon: ic.disabled,
                text: tx.disabled
            },
            // Figma Active+Disabled; unreachable here (disabled blocks :active), kept as the token source.
            activeDisabled: {
                background: bg.state.disabled3,
                icon: ic.disabled2,
                text: tx.tertiary
            }
        },
        // Figma: Button/Secondary/Outlined/* — grey-color outlined variant, distinct from
        // `secondary`. Default/Hover/Active backgrounds are gradients
        // (gradients/transparent-linear/*), same root cause as secondaryContained — confirmed
        // against the component spec (node 15341:101733/101739/101745).
        secondaryOutlined: {
            default: {
                background: toCssGradient(gr.transparentLinear.default),
                border: bd.default,
                icon: ic.heavy,
                text: tx.secondary
            },
            hover: {
                background: toCssGradient(gr.transparentLinear.hover)
            },
            active: {
                background: toCssGradient(gr.transparentLinear.active),
                border: bd.heavy,
                icon: ic.active,
                text: tx.primary
            },
            disabled: {
                border: bd.soft,
                icon: ic.disabled,
                text: tx.disabled
            },
            // Figma Active+Disabled; unreachable here (disabled blocks :active), kept as the token source.
            activeDisabled: {
                background: bg.state.disabledTransparent,
                border: bd.heavy,
                icon: ic.disabled2,
                text: tx.tertiary
            }
        },
        // Figma: Button/Secondary/Text/* — grey-color text variant, distinct from `text`.
        // Hover/Active backgrounds are gradients (gradients/transparent-linear/*), same root
        // cause as secondaryContained — confirmed against the component spec (node
        // 15341:102747/102753).
        secondaryText: {
            default: {
                icon: ic.heavy,
                text: tx.secondary
            },
            hover: {
                background: toCssGradient(gr.transparentLinear.hover)
            },
            active: {
                background: toCssGradient(gr.transparentLinear.active),
                icon: ic.active,
                text: tx.primary
            },
            disabled: {
                icon: ic.disabled,
                text: tx.disabled
            },
            // Figma Active+Disabled; unreachable here (disabled blocks :active), kept as the token source.
            activeDisabled: {
                background: bg.state.disabledTransparent,
                icon: ic.disabled2,
                text: tx.tertiary
            }
        },
        // Alias chain (Inverse/Button/Secondary/*): Active/Background -> Inverse/Background
        // Colors/State/Selected Transparent, Active/Border -> Inverse/Border Colors/Action 200,
        // Active/Icon -> Inverse/Icon Colors/Action Alt, Active/Text -> Inverse/Text Colors/
        // Action 100. The Figma "Inverse/..." variables are independently authored (see
        // invertedContained's Default/Icon note) — their resolved hex doesn't always match
        // `inverseBg/inverseBd/inverseIc/inverseTx` at the equivalently-named path, so each is
        // matched by hex against the theme-color dark objects rather than by name alone.
        invertedOutlined: {
            default: {
                border: inverseBd.heavy,
                icon: inverseIc.heavy,
                text: inverseTx.primary
            },
            hover: {
                background: inverseBg.state.hover0,
                border: inverseBd.heavy
            },
            active: {
                background: inverseBg.state.selectedTransparent,
                border: inverseBd.action200,
                icon: inverseIc.actionAlt,
                text: inverseTx.action100
            },
            disabled: {
                border: inverseBd.soft,
                icon: inverseIc.disabled,
                text: inverseTx.disabled
            },
            // Figma Active+Disabled; unreachable here (disabled blocks :active), kept as the token source.
            activeDisabled: {
                background: inverseBg.state.disabledTransparent,
                border: inverseBd.heavy,
                icon: inverseIc.disabled2,
                text: inverseTx.tertiary
            }
        },
        // NOTE: no Figma alias found under Inverse/Button/* for a "Text" variant — could not
        // verify against Figma Component Colors directly. Mirrors invertedOutlined.active
        // minus border, matching the established relationship elsewhere in this file where
        // `text.active` always equals `secondary.active` (outlined) minus border with the
        // same background/icon/text tokens.
        invertedText: {
            default: {
                icon: inverseIc.heavy,
                text: inverseTx.primary
            },
            hover: {
                background: inverseBg.state.hover0
            },
            active: {
                background: inverseBg.state.selectedTransparent,
                icon: inverseIc.actionAlt,
                text: inverseTx.action100
            },
            disabled: {
                icon: inverseIc.disabled,
                text: inverseTx.disabled
            }
        },
        // Verified against the Inverse/Button/Primary/* alias chain directly (not just by
        // resolved hex) — default/hover/active back to plain (non-inverse) Background Colors
        // tokens, not the inverseBg-derived values that were here before.
        invertedContained: {
            default: {
                background: bg.level50,
                icon: ic.heavy,
                text: inverseTx.action
            },
            hover: {
                background: bg.level300,
                icon: ic.active,
                text: inverseTx.action
            },
            active: {
                background: bg.state.selected3,
                icon: ic.active,
                text: inverseTx.action
            },
            disabled: {
                background: inverseBg.state.disabled2,
                icon: inverseIc.disabled,
                text: inverseTx.disabled2
            },
            // Figma Active+Disabled; unreachable here (disabled blocks :active), kept as the token source.
            activeDisabled: {
                background: inverseBg.state.disabled3,
                icon: inverseIc.disabled,
                text: inverseTx.tertiary
            }
        }
    };
};

//# sourceMappingURL=buttonColors.js.map